import { useUser } from '@context/user-context';
import { useActorPlan } from '@hooks/actor/use-actor-plan';
import { useCelloReferral } from '@hooks/cello/use-cello-referral';
import useMedia from '@hooks/use-media';
import {
  CELLO_REFERRAL_DISCOUNT_PERCENT,
  clearCachedCelloReferralCode,
} from '@modules/cello-referral';
import { useFeatureFlag } from '@modules/feature-flag';
import { createChildLogger } from '@modules/logger';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  Text,
} from '@ui-lib';
import { breakpoints } from '@ui-lib/css';
import { useRouter } from 'next/router';
import { useEffect, useId, useState } from 'react';

import styles from './CelloReferralUpgradeDialog.module.scss';
import { shouldShowCelloReferralOffer } from './cello-referral-offer';

const logger = createChildLogger({ module: 'components/CelloReferralUpgradeDialog' });

/**
 * Discounted upgrade offer for a user who opened a Cello referral link while
 * already signed in, and so never passes through the onboarding paywall. Taking
 * it hands off to the plans screen, which is where a plan is chosen and bought.
 *
 * Declining consumes the cached referral code, which is what stops the offer
 * being made again. Navigating away without answering leaves the code in place,
 * so the offer stands until the user actually responds to it.
 */
export const CelloReferralUpgradeDialog: React.FC = () => {
  const router = useRouter();
  const { loading: userLoading } = useUser();
  const { planType, loading: planLoading } = useActorPlan();
  const isMobile = useMedia([`(max-width: ${breakpoints.tablet - 1}px)`], [true], false);
  const [flagVariant, { loading: flagLoading, expose }] = useFeatureFlag(
    'cello-referral-paywall',
    'off',
    false,
  );
  const {
    isValid,
    referrerFirstName,
    referrerImageUrl,
    loading: referralLoading,
  } = useCelloReferral();
  const [dismissed, setDismissed] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [avatarLoaded, setAvatarLoaded] = useState(false);
  const titleId = useId();

  const resolving = flagLoading || referralLoading || planLoading || userLoading || !router.isReady;

  const showDialog =
    !resolving &&
    shouldShowCelloReferralOffer({
      flagEnabled: flagVariant === 'on',
      isReferralValid: isValid,
      isWelcomeOverlayUp: router.query.welcomeApp === '1',
      dismissed,
    });

  const isEligible = planType === 'free';

  // Only an actual offer counts as exposure. A subscriber who sees the "not
  // eligible" message was never shown the feature, and counting them would skew
  // the experiment toward a population that could never convert.
  useEffect(() => {
    if (!showDialog || !isEligible) {
      return;
    }

    expose();
  }, [showDialog, isEligible, expose]);

  function handleDismiss() {
    clearCachedCelloReferralCode();
    setDismissed(true);
  }

  function handleUpgrade() {
    // The plans screen owns plan choice and checkout, so this dialog is only the
    // hook into it. It deliberately leaves the referral code in place — that
    // screen is what consumes it.
    setNavigating(true);
    void router
      .push('/onboarding/referral-paywall?returning=1')
      .then((navigated) => {
        if (!navigated) {
          setNavigating(false);
        }
      })
      .catch((error) => {
        logger.error('Unable to open the Cello referral plans screen.', { error });
        setNavigating(false);
      });
  }

  if (!showDialog) {
    return <></>;
  }

  // Modal's `handleClose` is its unmount cleanup as well as its backdrop-click
  // handler — `Modal.tsx` returns it from an effect. Wiring it to `handleDismiss`
  // would consume the referral code on every unmount. Dismissal is explicit.

  // Nothing to sell here, so this stays on the standard dialog rather than the
  // offer sheet — the sheet's shape is there to present an offer.
  if (!isEligible) {
    return (
      <Modal open handleClose={() => undefined}>
        <div style={{ maxWidth: '565px' }}>
          <Dialog>
            <DialogHeader
              title="Oops, looks like your account is not eligible for this offer"
              onClose={handleDismiss}
            />
            <DialogContent>
              <Box paddingY="medium">
                <Text size="medium">
                  Your account already has a subscription so this referral discount cannot be
                  applied.
                </Text>
              </Box>
            </DialogContent>
            <DialogFooter>
              <Button onClick={handleDismiss}>Got it</Button>
            </DialogFooter>
          </Dialog>
        </div>
      </Modal>
    );
  }

  const title = referrerFirstName
    ? `${referrerFirstName} gave you ${CELLO_REFERRAL_DISCOUNT_PERCENT}% off`
    : `You've been given ${CELLO_REFERRAL_DISCOUNT_PERCENT}% off`;

  return (
    <Modal
      open
      handleClose={() => undefined}
      position={isMobile ? 'bottom' : 'center'}
      className={styles['above-breakpoint-overlay']}
    >
      <div className={styles['sheet']} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <span className={styles['handle']} aria-hidden />

        {referrerImageUrl ? (
          <span className={styles['avatar']}>
            <img
              className={[
                styles['avatar-image'],
                avatarLoaded ? styles['avatar-image-loaded'] : '',
              ].join(' ')}
              src={referrerImageUrl}
              alt=""
              aria-hidden
              fetchPriority="high"
              decoding="async"
              onLoad={() => setAvatarLoaded(true)}
              onError={() => setAvatarLoaded(true)}
            />
          </span>
        ) : null}

        <h2 className={styles['title']} id={titleId}>
          {title}
        </h2>
        <p className={styles['caption']}>Your discount applies automatically at checkout.</p>

        <div className={styles['actions']}>
          <Button
            variant="solid"
            size="regular"
            width="full"
            loading={navigating}
            onClick={handleUpgrade}
          >
            Claim {CELLO_REFERRAL_DISCOUNT_PERCENT}% off
          </Button>
          <Button
            variant="text"
            size="regular"
            width="full"
            disabled={navigating}
            onClick={handleDismiss}
          >
            No thanks
          </Button>
        </div>
      </div>
    </Modal>
  );
};
